import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Resource {
  type: string;
  title: string;
  description: string;
  image: string;
  alt: string;
  downloadSize?: string;
  readTime?: string;
  icon: string;
}

export default function SupportResources() {
  const resources: Resource[] = [
  {
    type: 'Technical Guide',
    title: 'Aerospace Manufacturing Standards Handbook',
    description: 'Comprehensive guide covering AS9100 requirements, material specifications, and quality standards for aerospace components.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0428591-1766493776287.png",
    alt: 'Open technical handbook with aerospace engineering diagrams and specifications on industrial desk',
    downloadSize: '12.4 MB',
    icon: 'DocumentTextIcon'
  },
  {
    type: 'Process Documentation',
    title: 'CNC Machining Capabilities Reference',
    description: 'Detailed documentation of CNC machining processes, tolerances, surface finishes, and material compatibility for aerospace applications.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_134f91a85-1772114243075.png",
    alt: 'Precision CNC machine cutting aerospace aluminum component with coolant spray in modern manufacturing facility',
    downloadSize: '8.7 MB',
    icon: 'CogIcon'
  },
  {
    type: 'Compliance Guide',
    title: 'ITAR Compliance & Security Protocols',
    description: 'Essential guide for defense contractors covering ITAR requirements, security procedures, and documentation standards.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1566d1e60-1764671587525.png",
    alt: 'Security compliance documents with official stamps and signatures on professional desk with American flag',
    downloadSize: '5.2 MB',
    icon: 'ShieldCheckIcon'
  },
  {
    type: 'Quality Manual',
    title: 'Quality Assurance & Testing Procedures',
    description: 'Complete quality management system documentation including inspection protocols, testing methods, and certification processes.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1de81f8cd-1767868446301.png",
    alt: 'Quality inspector examining aerospace component with precision measuring instruments in clean room environment',
    downloadSize: '9.8 MB',
    icon: 'BeakerIcon'
  },
  {
    type: 'Platform Guide',
    title: 'Client Portal User Manual',
    description: 'Step-by-step guide for using the Techaero platform including project management, tracking, and reporting features.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_122daf91b-1764676753294.png",
    alt: 'Modern computer screen displaying manufacturing project management dashboard with real-time analytics and charts',
    readTime: '15 min',
    icon: 'ComputerDesktopIcon'
  },
  {
    type: 'Material Guide',
    title: 'Aerospace Materials Selection Guide',
    description: 'Comprehensive reference for aerospace-grade materials including properties, applications, and processing requirements.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a229aeaf-1766493775523.png",
    alt: 'Various aerospace-grade metal samples including titanium and aluminum alloys arranged on technical specification sheets',
    downloadSize: '14.1 MB',
    icon: 'CubeIcon'
  }];


  return (
    <section className="py-20 bg-card">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 border border-accent/20 rounded-full mb-6">
            <Icon name="BookOpenIcon" size={20} className="text-accent" />
            <span className="font-cta text-sm text-accent tracking-wider">TECHNICAL RESOURCES</span>
          </div>

          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Comprehensive Documentation
            <span className="block text-accent mt-2">& Technical Guides</span>
          </h2>

          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Access detailed technical documentation, compliance guides, and manufacturing resources to support your aerospace projects
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {resources.map((resource, index) =>
          <div
            key={index}
            className="group bg-background border border-border rounded-xl overflow-hidden hover:border-accent transition-all duration-300 hover:shadow-glow cursor-pointer">

              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <AppImage
                src={resource.image}
                alt={resource.alt}
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent"></div>
                
                {/* Type Badge */}
                <div className="absolute top-4 left-4 px-3 py-1 bg-accent/90 backdrop-blur-sm rounded-full">
                  <span className="text-xs font-cta text-primary">{resource.type}</span>
                </div>

                {/* Icon */}
                <div className="absolute bottom-4 right-4 w-12 h-12 bg-card/90 backdrop-blur-sm rounded-lg flex items-center justify-center">
                  <Icon name={resource.icon as any} size={24} className="text-accent" />
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-headline text-xl font-bold text-foreground mb-3 group-hover:text-accent transition-colors duration-300">
                  {resource.title}
                </h3>

                <p className="text-sm text-technical-gray leading-relaxed mb-4">
                  {resource.description}
                </p>

                {/* Meta Info */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                    {resource.downloadSize &&
                  <>
                        <Icon name="ArrowDownTrayIcon" size={16} className="text-accent" />
                        <span>{resource.downloadSize}</span>
                      </>
                  }
                    {resource.readTime &&
                  <>
                        <Icon name="ClockIcon" size={16} className="text-accent" />
                        <span>{resource.readTime}</span>
                      </>
                  }
                  </div>

                  <div className="flex items-center text-accent text-sm font-cta group-hover:translate-x-2 transition-transform duration-300">
                    <span>DOWNLOAD</span>
                    <Icon name="ArrowRightIcon" size={16} className="ml-1" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* View All Resources */}
        <div className="text-center mt-12">
          <button className="px-8 py-3 bg-card border border-border text-foreground font-cta text-sm font-medium rounded-lg transition-all duration-300 hover:border-accent hover:shadow-glow flex items-center space-x-2 mx-auto">
            <span>VIEW ALL RESOURCES</span>
            <Icon name="ArrowRightIcon" size={20} />
          </button>
        </div>
      </div>
    </section>);

}